/**
 * "Who is this" card on the trend page of a person - a mini Wikipedia: photo, dates, what they are known for.
 * Data: GET /api/core/celebrities/profile/ (a web-searching model, cached on the server).
 */

import React, { useState } from 'react';
import { ExternalLink, User } from 'lucide-react';

import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Skeleton } from '../../components/ui/skeleton';

const MONTHS = [
  'января', 'февраля', 'марта', 'апреля', 'мая', 'июня',
  'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря',
];

// "1985-02-05" -> "5 февраля 1985", "1985" -> "1985"
const formatDate = (value) => {
  const [year, month, day] = value.split('-').map(Number);
  return month ? `${day} ${MONTHS[month - 1]} ${year}` : String(year);
};

const yearsWord = (n) => {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return 'год';
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return 'года';
  return 'лет';
};

// Full years from one "YYYY-MM-DD" to another (or to today); null when a date is only a year
const fullYears = (from, to) => {
  if (!from || from.length !== 10 || (to && to.length !== 10)) return null;
  const [fy, fm, fd] = from.split('-').map(Number);
  const now = new Date();
  const [ty, tm, td] = to ? to.split('-').map(Number) : [now.getFullYear(), now.getMonth() + 1, now.getDate()];
  const years = ty - fy - (tm < fm || (tm === fm && td < fd) ? 1 : 0);
  return years >= 0 ? years : null;
};

// "(81 год)" never breaks in the middle
const withAge = (date, years) => (years === null ? formatDate(date) : `${formatDate(date)} (${years}\u00a0${yearsWord(years)})`);

const hostname = (url) => {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return null;
  }
};

export const CelebrityProfileSkeleton = ({ className }) => (
  <Card className={className}>
    <CardHeader>
      <CardTitle>Кто это</CardTitle>
    </CardHeader>
    <CardContent>
      <div className="flex gap-4">
        <Skeleton className="h-32 w-24 shrink-0" />
        <div className="flex-1 space-y-2 pt-1">
          <Skeleton className="h-5 w-1/2" />
          <Skeleton className="h-4 w-3/4" />
          <Skeleton className="h-4 w-2/3" />
          <Skeleton className="h-4 w-1/2" />
        </div>
      </div>
      <p className="mt-4 text-sm text-muted-foreground">Собираем справку...</p>
    </CardContent>
  </Card>
);

const CelebrityProfileCard = ({ profile, className }) => {
  const [photoFailed, setPhotoFailed] = useState(false);

  const alive = !profile.death_date;
  const facts = [];
  if (profile.birth_date) {
    facts.push(['Дата рождения', alive ? withAge(profile.birth_date, fullYears(profile.birth_date)) : formatDate(profile.birth_date)]);
  }
  if (profile.death_date) {
    facts.push(['Дата смерти', withAge(profile.death_date, fullYears(profile.birth_date, profile.death_date))]);
  }
  if (profile.birth_place) facts.push(['Место рождения', profile.birth_place]);
  if (profile.country) facts.push(['Страна', profile.country]);
  if (profile.occupation) facts.push(['Род занятий', profile.occupation]);

  const knownFor = profile.known_for || [];
  const source = profile.wikipedia_url
    ? { url: profile.wikipedia_url, label: 'Википедия' }
    : (profile.sources || []).map((url) => ({ url, label: hostname(url) })).find((s) => s.label);

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle>Кто это</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Phone: facts go under the photo across the whole card; wider: next to the photo, under the name */}
        <div className="grid grid-cols-[6rem,1fr] gap-x-4 gap-y-3 sm:gap-y-2">
          {profile.photo_url && !photoFailed ? (
            <img
              src={profile.photo_url}
              alt={profile.name}
              loading="lazy"
              referrerPolicy="no-referrer"
              onError={() => setPhotoFailed(true)}
              className="h-32 w-24 rounded-md border bg-muted object-cover object-top sm:row-span-2"
            />
          ) : (
            <div className="flex h-32 w-24 items-center justify-center rounded-md border bg-muted text-muted-foreground sm:row-span-2">
              <User className="h-8 w-8" />
            </div>
          )}
          <div className="min-w-0 space-y-1">
            <div className="text-lg font-semibold leading-tight">{profile.name}</div>
            {profile.description && <div className="text-sm text-muted-foreground">{profile.description}</div>}
          </div>
          {facts.length > 0 && (
            <dl className="col-span-2 grid grid-cols-[auto,1fr] content-start gap-x-3 gap-y-1 text-sm sm:col-span-1 sm:col-start-2">
              {facts.map(([label, value]) => (
                <React.Fragment key={label}>
                  <dt className="text-muted-foreground">{label}</dt>
                  <dd className="min-w-0 break-words">{value}</dd>
                </React.Fragment>
              ))}
            </dl>
          )}
        </div>

        {knownFor.length > 0 && (
          <div>
            <div className="mb-1 text-sm font-medium">{profile.gender === 'female' ? 'Чем известна' : 'Чем известен'}</div>
            <ul className="list-disc space-y-0.5 pl-5 text-sm">
              {knownFor.map((item) => <li key={item}>{item}</li>)}
            </ul>
          </div>
        )}

        {profile.in_news && (
          <div>
            <div className="mb-1 text-sm font-medium">Почему в трендах</div>
            <p className="text-sm">{profile.in_news}</p>
          </div>
        )}

        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
          {source && (
            <a
              href={source.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center hover:text-primary transition-colors"
            >
              {source.label}
              <ExternalLink className="ml-1 h-3 w-3" />
            </a>
          )}
          <span>Собрано ИИ по открытым источникам - возможны неточности</span>
        </div>
      </CardContent>
    </Card>
  );
};

export default CelebrityProfileCard;
